<template>
  <v-content class="client ats page">
    <v-progress-linear
      v-show="!ready"
      indeterminate
      :color="themes.client.light.input"
      height="5"
      class="ma-0"
    />
    <v-container>
      <v-card v-if="opportunity && orderedPhases[0]">
        <v-toolbar
          height="80"
          extension-height="130"
          color="white"
          style="justify-content: center"
        >
          <v-layout flex row justify-space-between>
            <v-flex xs2 md1>
              <v-tooltip right v-if="loading" :value="true">
                <v-toolbar-side-icon slot="activator">
                  <v-icon
                    class="loading-icon"
                    :color="themes.client.light.input"
                    >mdi-loading</v-icon
                  >
                </v-toolbar-side-icon>
                <span>
                  Loaded <br />
                  {{ progressCurrent }} / <strong>{{ progressTotal }}</strong
                  ><br />
                  applications
                </span>
              </v-tooltip>
            </v-flex>
            <v-flex class="text-xs-center">
              <div class="headline">
                {{ opportunity.name }}
              </div>
              <div class="subheading" v-if="opportunity.organization">
                <span v-if="opportunity.organization.name">{{
                  opportunity.organization.name
                }}</span>
              </div>
            </v-flex>
            <v-flex xs2 md1 class="text-xs-right">
              <v-menu
                offset-y
                left
                max-height="30%"
                nudge-right="24"
                nudge-bottom="24"
                :disabled="opportunities.length < 2"
              >
                <div class="headline" slot="activator">
                  <v-icon medium v-if="opportunities.length > 1"
                    >mdi-chevron-down</v-icon
                  >
                </div>
                <v-list>
                  <v-list-tile
                    v-for="(opp, i) in managedOpportunities"
                    :key="i"
                    @click="
                      phase = null;
                      $router.replace(
                        `/client/opportunities/${opp.id}/applications`
                      );
                    "
                  >
                    <v-list-tile-title>{{ opp.name }}</v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-flex>
          </v-layout>

          <div
            slot="extension"
            class="client ats phase tabs"
            style="position: relative; width: 100%"
            v-if="get(phase, 'name')"
          >
            <v-tabs
              ref="tabs"
              centered
              :value="phase.name"
              color="transparent"
              hide-slider
              height="130"
              next-icon=""
              prev-icon=""
            >
              <v-tab
                v-for="tab in [...orderedPhases, ...pushedPhases]"
                :key="tab.name"
                :href="`#${tab.name}`"
              >
                <v-tooltip bottom>
                  <span>{{ tab.description }}</span>
                  <v-card
                    :class="{ 'phase-card': tab.name !== phase.name }"
                    :dark="tab.name === phase.name"
                    class="text-xs-center py-3"
                    :height="130"
                    :width="200"
                    flat
                    tile
                    slot="activator"
                  >
                    <v-layout
                      column
                      fill-height
                      class="pt-2"
                      @click="changePhase(tab)"
                    >
                      <v-flex>
                        <v-badge :color="themes.client.light.badge" left>
                          <span slot="badge">
                            <strong>{{
                              (counts && counts[tab.name]) || 0
                            }}</strong>
                          </span>
                          <v-icon large> mdi-account-group </v-icon>
                        </v-badge>
                      </v-flex>
                      <v-flex>
                        <v-card-text class="subheading py-0">
                          {{ tab.name }}
                        </v-card-text>
                      </v-flex>
                    </v-layout>
                    <div
                      v-if="tab.name === phase.name"
                      :class="themes.client.light.input"
                      class="bottom-border"
                    ></div>
                  </v-card>
                </v-tooltip>
              </v-tab>
              <v-menu v-model="showMoreMenu" offset-y left class="v-tabs__div">
                <v-card
                  slot="activator"
                  class="text-xs-center py-3 v-tabs__item phase-card"
                  :class="{ 'phase-card--active': showMoreMenu }"
                  :height="130"
                  :width="200"
                  tile
                  flat
                >
                  <v-layout column fill-height class="pt-2">
                    <v-flex>
                      <v-icon large> mdi-chevron-down </v-icon>
                    </v-flex>
                    <v-flex>
                      <v-card-text class="subheading py-0"> MORE </v-card-text>
                    </v-flex>
                  </v-layout>
                </v-card>
                <v-list>
                  <v-list-tile
                    v-for="tab in pushablePhases"
                    :key="`more-${tab.name}`"
                    @click="changePhase(tab)"
                  >
                    <v-list-tile-avatar>
                      <v-icon> mdi-account-group </v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content class="pr-1">
                      <v-list-tile-title>{{ tab.name }}</v-list-tile-title>
                    </v-list-tile-content>
                    <v-list-tile-action>
                      <v-chip :color="themes.client.light.chip" small>
                        <strong>{{ (counts && counts[tab.name]) || 0 }}</strong>
                      </v-chip>
                    </v-list-tile-action>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-tabs>
            <v-btn
              v-if="prevIconVisible"
              @click="scrollLeft"
              fab
              small
              class="scroll-button left"
            >
              <v-icon large>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              v-if="nextIconVisible"
              @click="scrollRight"
              fab
              small
              class="scroll-button right"
            >
              <v-icon large>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-toolbar>
        <ApplicationsListData :filters="filters" :loading="loading">
          <v-card slot-scope="{ applications }" class="elevation-0">
            <ApplicationsToolbar :applications="applications" :phase="phase" />
            <v-tabs-items
              v-if="get(phase, 'name')"
              :value="phase.name"
              @input="phase"
            >
              <v-tab-item
                v-for="tab in [...orderedPhases, ...pushedPhases]"
                :key="tab.name"
                :value="tab.name"
                lazy
              >
                <ApplicationCard
                  v-for="application in applications"
                  :key="`application-${application.id}`"
                  :application="application"
                  :phase="phase"
                  class="mb-0"
                  @click="toggleView"
                />
              </v-tab-item>
            </v-tabs-items>
            <ApplicationDialog
              v-if="view"
              :id="parseInt(view)"
              @close="toggleView($event)"
            />
          </v-card>
        </ApplicationsListData>
      </v-card>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from "@/mixins/managedDocumentAttributes.js";
import { managedQueryParameters } from "@/mixins/managedQueryParameters.js";

// Components
import ApplicationCard from "@/components/client/applications/ApplicationCard";
import ApplicationDialog from "@/components/client/applications/ApplicationDialog";
import ApplicationsListData from "@/components/client/applications/ApplicationsListData";
import ApplicationsToolbar from "@/components/client/applications/ApplicationsToolbar";

// Helpers
const bodyScroll = require("body-scroll-toggle");
import _ from "lodash";
import { mapGetters, mapState } from "vuex";

export default {
  mixins: [managedDocumentAttributes, managedQueryParameters],

  components: {
    ApplicationCard,
    ApplicationDialog,
    ApplicationsListData,
    ApplicationsToolbar
  },

  created() {
    this.refresh();
  },

  beforeDestroy() {
    this.$store.dispatch("client/applications/abandon");
    this.$store.dispatch("client/applications/resetState");

    if (this.resizeObserver && _.get(this, "$refs['tabs'].$el")) {
      this.resizeObserver.unobserve(this.$refs["tabs"].$el);
    }
  },

  props: ["id"],

  data() {
    return {
      currentTabIndex: 0,
      pushedPhases: [],

      // Toggles
      nextIconVisible: false,
      prevIconVisible: false,
      ready: false,
      showMoreMenu: false,

      filters: {},
      phase: null,
      view: null
    };
  },

  computed: {
    orderedPhases() {
      return this.$store.getters["client/opportunities/orderedPhases"]();
    },

    opportunity() {
      return this.$store.getters["client/opportunities/find"]();
    },
    user() {
      return this.$store.getters["auth/user"];
    },
    ...mapState("client/opportunities", {
      opportunities: "all",
      counts: "counts",
      loadingOpportunities: "loading"
    }),
    managedOpportunities() {
      if (this.user.primary) return this.opportunities;

      return this.opportunities
        .filter(o => o.managers.some(m => m.id === this.user.id))
        .sort(({ name: a }, { name: b }) => {
          if (a > b) return 1;
          if (a < b) return -1;
          return 0;
        });
    },

    ...mapState("client/applications", {
      loadingApplications: "loading"
    }),

    ...mapGetters("client/applications", {
      progress: "progress",
      progressCurrent: "progressCurrent",
      progressTotal: "progressTotal"
    }),

    loading() {
      return this.loadingOpportunities || this.loadingApplications;
    },

    moreApplicationPhases() {
      if (this.opportunity && this.opportunity.syntheticPhases) {
        return this.opportunity.syntheticPhases;
      }
      return [];
    },

    pushablePhases() {
      if (this.moreApplicationPhases.length) {
        return this.moreApplicationPhases.filter(
          available =>
            ![...this.orderedPhases, ...this.pushedPhases].some(
              unavailable => available.name === unavailable.name
            )
        );
      }
      return [];
    }
  },

  watch: {
    id: function(value) {
      this.refresh();
    },

    phase: {
      handler: "setScrollButtonAttributes"
    },

    view: function(value) {
      // When the modal is shown
      if (value) {
        bodyScroll.disable();
      }

      // When the modal is hidden
      if (!value) {
        bodyScroll.enable();
      }
    }
  },

  methods: {
    async refresh() {
      // Clear old applications
      await this.$store.dispatch("client/applications/resetState");
      // Fetch all opportunities
      await this.$store.dispatch("client/opportunities/fetchAll");
      // Fetch opportunity
      await this.$store
        .dispatch("client/opportunities/fetch", this.id)
        .then(() => {
          this.setDocumentAttributes();

          // Set initial phase
          if (
            "status" in this.initialQueryParameters &&
            this.findPhase(this.initialQueryParameters.status[0])
          ) {
            this.changePhase(this.initialQueryParameters.status[0]);
          } else if (this.orderedPhases[0] && !this.phase) {
            this.changePhase(this.orderedPhases[0]);
          }

          // Fetch applicant and update filters
          if (this.$route.query["view"]) {
            this.$store
              .dispatch("client/applications/fetch", {
                id: this.$route.query["view"]
              })
              .then(({ data }) => {
                if (data.status !== this.phase) {
                  this.changePhase(data.status);
                }
                this.view = data.id;
              });
          }
        });
      this.initScrollButtonAttributes();
      // Load applications
      if (!this.loadingApplications)
        this.$store
          .dispatch("client/applications/load", {
            opportunity: this.$route.params.id
          })
          .then(() =>
            this.$store.dispatch("client/applications/updateFilters", {
              ...this.filters,
              ...this.params
            })
          );
      this.ready = true;
      // Fetch chat messages for counts on cards
      this.$store.dispatch("client/chats/fetchAll");
    },
    setDocumentAttributes() {
      if (_.get(this, "opportunity.name")) {
        this.documentAttributes.title = `${this.opportunity.name} ATS - Leaply`;
      } else {
        setTimeout(this.setDocumentAttributes, 100);
      }
    },

    get(object, path) {
      return _.get(object, path);
    },

    changePhase(phase) {
      const newPhase = this.findPhase(phase);
      if (newPhase) {
        this.pushPhase(newPhase);
        this.filters.page = 1;
        this.updateFilters({ status: newPhase.name });
        this.phase = newPhase;
      }
    },

    findPhase(
      phase,
      phases = [...this.orderedPhases, ...this.moreApplicationPhases]
    ) {
      if (Number(phase)) {
        return phases.find(p => p.id === Number(phase));
      }
      if (phase && phase.id) {
        return phases.find(p => p.id === phase.id);
      }
      if (phase && phase.name) {
        return phases.find(p => p.name === phase.name);
      }
      return phases.find(p => p.name === phase);
    },

    pushPhase(phase) {
      const newPhase = this.findPhase(phase, this.pushablePhases);
      if (newPhase) {
        this.pushedPhases.push(newPhase);
      }
    },

    updateFilters(changes) {
      if (changes) {
        Object.keys(changes).forEach(change => {
          this.$set(this.filters, change, changes[change]);
        });

        this.updateQueryParameters(changes);
      }
    },

    initScrollButtonAttributes() {
      if (!this.$refs["tabs"]) setTimeout(this.initScrollButtonAttributes, 100);
      this.setScrollButtonAttributes();
    },

    setScrollButtonAttributes() {
      if (!this.$refs["tabs"]) {
        return;
      }

      if (!this.resizeObserver && !!window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(
          this.setScrollButtonAttributes
        );
        this.resizeObserver.observe(this.$refs["tabs"].$el);
      }

      this.$nextTick(() => {
        this.nextIconVisible = this.$refs["tabs"].checkNextIcon();
        this.prevIconVisible = this.$refs["tabs"].checkPrevIcon();
      });
    },

    scrollRight() {
      this.$refs["tabs"].scrollTo("next");
      this.setScrollButtonAttributes();
    },

    scrollLeft() {
      this.$refs["tabs"].scrollTo("prev");
      this.setScrollButtonAttributes();
    },

    toggleView(id = null) {
      this.view = id;
      this.updateQueryParameters({ view: this.view });
    }
  }
};
</script>

<style>
.client.ats.page div.overlay {
  position: relative;
}

.client.ats.page .v-window .v-window__container {
  height: unset !important;
}

.client.ats.page div.overlay:after {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2;
}

.client.ats.page .mdi-loading {
  animation: spin-animation 1.2s infinite linear;
}

.client.ats.page div.v-toolbar__content {
  justify-content: center;
}
.client.ats.page div.v-toolbar__extension,
.client.ats.page div.v-toolbar__extension .v-tabs__item {
  padding-left: 0;
  padding-right: 0;
}

.client.ats.page .client.ats.phase.tabs .ats-tabs {
  max-width: 100%;
  overflow-x: scroll;
  cursor: pointer;
  scroll-behavior: smooth;
}
.client.ats.page .client.ats.phase.tabs .bottom-border {
  position: absolute;
  width: 100%;
  height: 0.5rem;
  bottom: 0px;
}

.client.ats.page .client.ats.phase.tabs .phase-card {
  background-color: rgba(0, 0, 0, 0);
  border: 0px;
}

.client.ats.page .client.ats.phase.tabs .phase-card:hover,
.client.ats.page .client.ats.phase.tabs .phase-card--active {
  background-color: rgba(0, 0, 0, 0.2);
}

.client.ats.page .client.ats.phase.tabs .scroll-button {
  position: absolute;
  top: 50%;
}
.client.ats.page .client.ats.phase.tabs .scroll-button.left {
  left: 0px;
  transform: translate(-50%, -50%);
}
.client.ats.page .client.ats.phase.tabs .scroll-button.right {
  right: 0px;
  transform: translate(50%, -50%);
}

.client.ats.page .client.ats.phase.tabs .v-tabs__wrapper--show-arrows {
  margin-right: 0px;
  margin-left: 0px;
}

.client.ats.page .client.ats.phase.tabs .v-badge__badge {
  width: 100%;
  border-radius: 20px;
}

@keyframes spin-animation {
  0% {
    transform: rotate(359deg);
  }
  100% {
    transform: rotate(0deg);
  }
}
</style>
